// Booking's command boundary (rentals and sponsorships plan, Phase 2; ADR
// 0043). Booking stays the system of record and the engine (model A): Hub
// asks, Booking decides. Every command is applied by the same domain
// function Booking's own screens use, so the two can't drift.
//
// Two channels, one command log in Booking (`source_commands`):
//
//   staff     Hub -> Booking, POST /api/internal/hub/v1/commands (now)
//   customer  myBRE -> Booking, /api/internal/mybre/v1/* (designed here;
//             Booking refuses every request until Phase 3 turns it on)
//
// ---------------------------------------------------------- the staff channel
//
//   POST <BOOKING_HUB_URL>/api/internal/hub/v1/commands
//   authorization:      Bearer <Booking's HUB_COMMAND_SECRET>   (never the read key)
//   x-hub-request-id:   <uuid>
//   x-hub-environment:  production | development   (Booking refuses a mismatch)
//   x-hub-timestamp:    unix seconds
//   x-hub-nonce:        16-64 chars of [A-Za-z0-9_-], new for every request
//   x-hub-signature:    base64url(HMAC-SHA256(HUB_COMMAND_SIGNING_SECRET, canonical)), no padding
//
//   canonical = [
//     'bre-booking-command-v1',
//     'POST',
//     path,               // exactly /api/internal/hub/v1/commands
//     timestamp,
//     nonce,
//     sha256Hex(body),    // of the exact body bytes
//   ].join('\n')
//
// Booking checks the bearer, the signature in constant time, a timestamp
// within BOOKING_COMMAND_MAX_SKEW_SECONDS of its clock, and that it has never
// seen the nonce (kept for twice the window). Reads stay bearer-only
// (decision 16). Both keys are Booking's and Hub's alone, a different value
// per environment; neither is ever the read key.
//
// The body is a BookingCommand. Booking then:
//   1. replays: the same commandId returns the first result with status
//      'duplicate' (a refusal is replayed as the same refusal); the same id
//      with a different body is COMMAND_REUSED;
//   2. re-checks the actor (rule 10): actor.sub is matched to Booking's
//      users.bre_user_id and that user's Booking role must grant the
//      command's Booking capability (BOOKING_COMMANDS[type].bookingCapability);
//      a Hub capability alone moves nothing;
//   3. checks expected.version against the record's newest change-feed seq
//      (the `version` every Hub read carries); a mismatch is STALE with the
//      current version, never "last write wins";
//   4. applies the domain function, writing the change, the command log row
//      and the actor on the change feed in one D1 batch (the version check
//      is repeated inside that batch, so two racing commands can't both win).

import { z } from 'zod'
import { BookingInviteBatchResult, INVITE_BATCH_CAP } from './source-booking-retirement'

export const BOOKING_COMMANDS_PATH = '/api/internal/hub/v1/commands'
export const BOOKING_AVAILABILITY_PATH = '/availability'
export const BOOKING_STAFF_PATH = '/staff'
export const BOOKING_COMMAND_LOG_PATH = '/commands'
export const BOOKING_COMMAND_SIGNATURE_VERSION = 'bre-booking-command-v1'
export const BOOKING_COMMAND_MAX_SKEW_SECONDS = 300
export const BOOKING_COMMAND_HEADERS = {
  requestId: 'x-hub-request-id',
  environment: 'x-hub-environment',
  timestamp: 'x-hub-timestamp',
  nonce: 'x-hub-nonce',
  signature: 'x-hub-signature',
} as const

// The contract pin (plan §7.1.9): Booking's copy (booking-commands.js) says
// the same number, and both sides test the vector below.
export const BOOKING_COMMAND_CONTRACT_VERSION = 1

const encoder = new TextEncoder()

function base64url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

async function sha256Hex(body: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(body))
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export function bookingCommandCanonical(input: { path: string; timestamp: string; nonce: string; bodySha256: string }): string {
  return [BOOKING_COMMAND_SIGNATURE_VERSION, 'POST', input.path, input.timestamp, input.nonce, input.bodySha256].join('\n')
}

// Hub signs every command. The signing secret must be at least 32 characters.
export async function signBookingCommand(input: { signingSecret: string; body: string; now?: number; nonce?: string; path?: string }): Promise<{ timestamp: string; nonce: string; signature: string }> {
  if (!input.signingSecret || input.signingSecret.length < 32) throw new Error('The Booking command signing secret must be at least 32 characters')
  const timestamp = String(Math.floor((input.now ?? Date.now()) / 1000))
  const nonce = input.nonce ?? base64url(crypto.getRandomValues(new Uint8Array(18)))
  const key = await crypto.subtle.importKey('raw', encoder.encode(input.signingSecret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
  const canonical = bookingCommandCanonical({ path: input.path ?? BOOKING_COMMANDS_PATH, timestamp, nonce, bodySha256: await sha256Hex(input.body) })
  const signature = base64url(new Uint8Array(await crypto.subtle.sign('HMAC', key, encoder.encode(canonical))))
  return { timestamp, nonce, signature }
}

// The shared vector: Booking's booking-commands.test.js checks the same
// inputs give the same signature, so the two copies stay honest.
export const BOOKING_COMMAND_TEST_VECTOR = {
  signingSecret: 'test-signing-secret-0123456789abcdef-0123456789',
  body: '{"version":1,"commandId":"00000000-0000-4000-8000-000000000001","type":"booking.rental.message.send"}',
  now: 1_792_000_000_000,
  nonce: 'nonce-0123456789abcdef',
  signature: 'mW_MZt33hjWjAo55m7aBT71mR4W1jwbTqIEGXHNVVco',
} as const

// ------------------------------------------------------------- the commands

export type BookingCommandTargetKind = 'rental' | 'sponsorship' | 'company'

// Booking's role capabilities (Booking permissions.ts), for the commands Hub
// sends: admin has all four; booker has rentals.manage and
// sponsorships.manage; production has rentals.manage; finance has
// invoices.manage and collections.manage; read_only has none. Booking checks
// its own table; this copy is for Hub's words ("needs the finance role").
export type BookingCapability = 'rentals.manage' | 'sponsorships.manage' | 'invoices.manage' | 'collections.manage'
export const BOOKING_CAPABILITIES = ['rentals.manage', 'sponsorships.manage', 'invoices.manage', 'collections.manage'] as const

export const BOOKING_ROLES = ['admin', 'booker', 'production', 'finance', 'read_only'] as const
export type BookingRole = (typeof BOOKING_ROLES)[number]
export const BOOKING_ROLE_CAPABILITIES: Record<BookingRole, BookingCapability[]> = {
  admin: ['rentals.manage', 'sponsorships.manage', 'invoices.manage', 'collections.manage'],
  booker: ['rentals.manage', 'sponsorships.manage'],
  production: ['rentals.manage'],
  finance: ['invoices.manage', 'collections.manage'],
  read_only: [],
}

// Hub's own capability for a command (both must allow it, plan §8.3).
export type BookingHubCapability = 'rentals.manage' | 'sponsorships.manage' | 'rentals.money'

type TargetSpec = BookingCommandTargetKind | readonly ('rental' | 'sponsorship')[] | null

type Spec = {
  // The record a command is about: one kind, either a rental or a
  // sponsorship (the money commands: an invoice belongs to one of them), or
  // none (a create).
  target: TargetSpec
  // A command on an existing record must say which version the person saw.
  // The refund is the one exception: it is checked against the payment's
  // refunds instead (see booking.refund.execute below).
  needsExpected: boolean
  bookingCapability: BookingCapability
  hubCapability: BookingHubCapability
  // Moves or records money: the person needs rentals.money in Hub, the
  // answer is shown as Booking's, and Hub never calls it settled until
  // Booking says so.
  money: boolean
  phase: 2 | 4 | 5 | 6 | 7 | 8
  label: string
}

const RENTAL = 'rental' as const
const SPONSORSHIP = 'sponsorship' as const
const RECORD = ['rental', 'sponsorship'] as const

export const BOOKING_COMMANDS = {
  // Phase 2 (ADR 0043)
  'booking.rental.create': { target: null, needsExpected: false, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 2, label: 'Create a rental inquiry' },
  'booking.rental.owner.set': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 2, label: 'Set the owner' },
  'booking.rental.status.set': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 2, label: 'Change the status' },
  'booking.rental.stage_move': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 2, label: 'Move on the board' },
  'booking.sponsorship.stage_move': { target: SPONSORSHIP, needsExpected: true, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 2, label: 'Move on the board' },
  'booking.rental.renter.set': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 2, label: 'Change the renter' },
  'booking.rental.details.update': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 2, label: 'Edit the event details' },
  'booking.rental.message.send': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 2, label: 'Post a message to the renter' },
  'booking.rental.mybre.invite': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 2, label: 'Send or resend the portal invite' },
  'booking.hold.place': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 2, label: 'Place a hold' },
  'booking.hold.extend': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 2, label: 'Extend a hold' },
  'booking.hold.release': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 2, label: 'Release a hold' },
  'booking.sponsorship.create': { target: null, needsExpected: false, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 2, label: 'Create a sponsorship prospect' },
  'booking.sponsorship.owner.set': { target: SPONSORSHIP, needsExpected: true, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 2, label: 'Set the owner' },
  'booking.sponsorship.message.send': { target: SPONSORSHIP, needsExpected: true, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 2, label: 'Post a message to the sponsor' },
  'booking.sponsorship.mybre.invite': { target: SPONSORSHIP, needsExpected: true, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 2, label: 'Send or resend the portal invite' },
  'booking.company.ensure': { target: null, needsExpected: false, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 2, label: 'Find or create a company' },

  // Phase 4: quotes, contracts, insurance and documents (staff)
  'booking.rental.space.add': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Add a space to the quote' },
  'booking.rental.space.update': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Change a space’s times or price' },
  'booking.rental.space.remove': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Remove a space from the quote' },
  'booking.rental.lines.set': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Save the quote’s line items' },
  'booking.rental.pricing.set': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Set the discount and deposit' },
  'booking.rental.estimate.send': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Send the estimate' },
  'booking.rental.contract.send': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Generate and send the contract' },
  'booking.rental.contract.countersign': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Countersign the contract for BRE' },
  'booking.rental.coi.verify': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Verify the certificate of insurance' },
  'booking.rental.coi.reject': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Reject the certificate of insurance' },
  'booking.rental.coi.request': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Ask the renter for a certificate of insurance' },
  'booking.rental.insurance.offers.set': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Offer Vertical Insure coverage' },
  'booking.rental.document.request': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Request a document' },
  'booking.rental.document.review': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 4, label: 'Approve or waive a requested document' },
  'booking.sponsorship.agreement.terms.set': { target: SPONSORSHIP, needsExpected: true, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 4, label: 'Choose the agreement’s terms' },
  'booking.sponsorship.agreement.send': { target: SPONSORSHIP, needsExpected: true, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 4, label: 'Send the agreement for signature' },

  // Phase 5: money (staff). Booking moves the money and posts to
  // QuickBooks exactly as its own screens do; Hub only asks.
  'booking.invoice.create': { target: RECORD, needsExpected: true, bookingCapability: 'invoices.manage', hubCapability: 'rentals.money', money: true, phase: 5, label: 'Create an invoice' },
  'booking.invoice.approve': { target: RECORD, needsExpected: true, bookingCapability: 'collections.manage', hubCapability: 'rentals.money', money: true, phase: 5, label: 'Approve an invoice for sending' },
  'booking.invoice.send': { target: RECORD, needsExpected: true, bookingCapability: 'invoices.manage', hubCapability: 'rentals.money', money: true, phase: 5, label: 'Send an invoice' },
  'booking.invoice.remind': { target: RECORD, needsExpected: true, bookingCapability: 'collections.manage', hubCapability: 'rentals.money', money: true, phase: 5, label: 'Send an invoice reminder' },
  'booking.invoice.void': { target: RECORD, needsExpected: true, bookingCapability: 'invoices.manage', hubCapability: 'rentals.money', money: true, phase: 5, label: 'Void an invoice' },
  'booking.invoice.collection.update': { target: RECORD, needsExpected: true, bookingCapability: 'collections.manage', hubCapability: 'rentals.money', money: true, phase: 5, label: 'Update collections follow-up' },
  'booking.payment.record_offline': { target: RECORD, needsExpected: true, bookingCapability: 'invoices.manage', hubCapability: 'rentals.money', money: true, phase: 5, label: 'Record a check, cash or wire payment' },
  'booking.refund.execute': { target: RECORD, needsExpected: false, bookingCapability: 'invoices.manage', hubCapability: 'rentals.money', money: true, phase: 5, label: 'Carry out an approved refund' },
  'booking.sponsorship.invoices.generate': { target: SPONSORSHIP, needsExpected: true, bookingCapability: 'invoices.manage', hubCapability: 'rentals.money', money: true, phase: 5, label: 'Generate the sponsorship’s invoices' },

  // Phase 6 (ADR 0046): the post-event inspection and closing the record.
  // Closing moves no money; Booking works its checklist out inside the command.
  'booking.rental.closeout.save': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 6, label: 'Record the post-event inspection' },
  'booking.rental.closeout.complete': { target: RENTAL, needsExpected: true, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 6, label: 'Close the rental record' },

  // Phase 7 (ADR 0046): deliverables, renewals and converting interest.
  'booking.deliverable.add': { target: SPONSORSHIP, needsExpected: true, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 7, label: 'Add a deliverable' },
  'booking.deliverable.update': { target: SPONSORSHIP, needsExpected: true, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 7, label: 'Update a deliverable' },
  'booking.deliverable.review': { target: SPONSORSHIP, needsExpected: true, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 7, label: 'Review the sponsor’s submission' },
  'booking.deliverable.remove': { target: SPONSORSHIP, needsExpected: true, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 7, label: 'Remove a deliverable' },
  'booking.sponsorship.renew': { target: SPONSORSHIP, needsExpected: true, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 7, label: 'Create the renewal offer' },
  'booking.sponsorship.renewal.set': { target: SPONSORSHIP, needsExpected: true, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 7, label: 'Update the renewal terms' },
  'booking.interest.convert': { target: null, needsExpected: false, bookingCapability: 'sponsorships.manage', hubCapability: 'sponsorships.manage', money: false, phase: 7, label: 'Convert interest into a sponsorship' },

  // Phase 8 (ADR 0047): myBRE invites to every customer with business in
  // the next 90 days who is neither linked nor invited. Booking re-checks the
  // preview it gave (previewHash), caps and paces the batch, and skips
  // anyone already invited.
  'booking.mybre.invite_batch': { target: null, needsExpected: false, bookingCapability: 'rentals.manage', hubCapability: 'rentals.manage', money: false, phase: 8, label: 'Send myBRE invites to customers not yet linked' },
} as const satisfies Record<string, Spec>

export type BookingCommandType = keyof typeof BOOKING_COMMANDS
export const BOOKING_COMMAND_TYPES = Object.keys(BOOKING_COMMANDS) as BookingCommandType[]

export const isBookingCommandType = (v: unknown): v is BookingCommandType => typeof v === 'string' && v in BOOKING_COMMANDS

// The record kinds a command may be about ([] for a create).
export function bookingCommandTargets(type: BookingCommandType): BookingCommandTargetKind[] {
  const t = BOOKING_COMMANDS[type].target as TargetSpec
  if (t === null) return []
  return typeof t === 'string' ? [t] : [...t]
}

// Which Booking roles may run a command (for the runbook's table and the
// refusal message).
export function bookingRolesFor(type: BookingCommandType): BookingRole[] {
  const needed = BOOKING_COMMANDS[type].bookingCapability
  return BOOKING_ROLES.filter((role) => BOOKING_ROLE_CAPABILITIES[role].includes(needed))
}

// ------------------------------------------------------- the discount rule

// Decision 6 (ADR 0041): a discount over 15% of the quote's subtotal, or
// over $500, needs a Hub approval (ADR 0004) from someone other than the
// person asking, before Booking applies it. Booking applies the same rule
// to a discount that comes from Hub and refuses one without an approval
// (APPROVAL_REQUIRED). Strict (owner, 2026-10-16): the rule applies to the
// resulting discount of every pricing command, so a deposit or note change
// that keeps an over-threshold discount needs an approval too. Booking's own
// pricing screen is unchanged.
export const DISCOUNT_APPROVAL_PERCENT = 15
export const DISCOUNT_APPROVAL_MINOR = 50_000
export function discountNeedsApproval(input: { subtotalMinor: number; discountMinor: number }): boolean {
  const discount = Math.max(0, Math.round(input.discountMinor))
  if (discount === 0) return false
  if (discount > DISCOUNT_APPROVAL_MINOR) return true
  const subtotal = Math.max(0, Math.round(input.subtotalMinor))
  // Over 15%: discount * 100 > subtotal * 15, in whole cents (no float).
  return discount * 100 > subtotal * DISCOUNT_APPROVAL_PERCENT
}

// The approval a refund or an over-threshold discount carries to Booking:
// Hub's approval id, who asked and who approved (BRE subjects; never the
// same person), and when. Booking refuses a missing one, and one whose
// approver is the requester.
export const BookingMoneyApproval = z.object({
  id: z.string().min(1).max(64),
  requestedBySub: z.string().min(1).max(200),
  approvedBySub: z.string().min(1).max(200),
  approvedAt: z.string().min(10).max(40),
})
export type BookingMoneyApproval = z.infer<typeof BookingMoneyApproval>

// Staff may move a rental only between the statuses that move no money or
// paperwork (plan §7.2): inquiry <-> estimate, and cancel before a contract.
// Everything else stays in Booking until Phases 4 and 5. Booking applies the
// same rule (and, inside it, its own VALID_TRANSITIONS).
export const HUB_RENTAL_STATUS_MOVES: Record<string, string[]> = {
  inquiry: ['estimate', 'cancelled'],
  estimate: ['inquiry', 'cancelled'],
}

const Text = (min: number, max: number) => z.string().trim().min(min).max(max)
const OptText = (max: number) => z.string().trim().max(max).nullable().optional()
const Email = z.string().trim().max(320).regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const Id = z.string().min(1).max(64)
const Sub = z.string().min(1).max(200)

// Phase 4 and 5 shapes. Money is whole cents (Booking keeps REAL dollars and
// converts at its edge, rounding to the cent). A room's times are Booking's
// local wall time (America/Chicago) as 'YYYY-MM-DDTHH:MM', the form Booking's
// own screens send.
const Minor = z.number().int().min(0).max(100_000_000)
const PositiveMinor = z.number().int().min(1).max(100_000_000)
const LocalTime = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'Use YYYY-MM-DDTHH:MM (local time)')
// Up to two decimals (Booking's quantities are hours, days or units).
const Quantity = z
  .number()
  .positive()
  .max(10_000)
  .refine((v) => Math.abs(Math.round(v * 100) - v * 100) < 1e-9, 'At most two decimal places')
const SPACE_RATE_TYPES = ['hourly', 'half_day', 'full_day', 'custom'] as const
const SPACE_DAY_TYPES = ['event', 'setup', 'cleanup'] as const
const SpaceTimes = {
  startLocal: LocalTime,
  endLocal: LocalTime,
  rateType: z.enum(SPACE_RATE_TYPES),
  rateMinor: Minor.nullable(),
  quantity: Quantity,
  notes: OptText(1000),
  dayType: z.enum(SPACE_DAY_TYPES),
  // The event's own window inside the reserved block (event days only).
  eventStartLocal: LocalTime.nullable().optional(),
  eventEndLocal: LocalTime.nullable().optional(),
}
const endsAfterStart = (p: { startLocal: string; endLocal: string; eventStartLocal?: string | null; eventEndLocal?: string | null }, ctx: z.RefinementCtx) => {
  if (p.endLocal <= p.startLocal) ctx.addIssue({ code: 'custom', path: ['endLocal'], message: 'End must be after start' })
  if (p.eventStartLocal && p.eventEndLocal && p.eventEndLocal <= p.eventStartLocal) ctx.addIssue({ code: 'custom', path: ['eventEndLocal'], message: 'Event end must be after event start' })
}
export const BOOKING_COLLECTION_STATUSES = ['current', 'follow_up', 'promise_to_pay', 'dispute', 'escalated', 'uncollectible'] as const
export type BookingCollectionStatus = (typeof BOOKING_COLLECTION_STATUSES)[number]
export const OFFLINE_PAYMENT_METHODS = ['check', 'cash', 'wire', 'other'] as const
export const REFUND_METHODS = ['stripe', 'check', 'cash', 'wire', 'other'] as const
export type BookingRefundMethod = (typeof REFUND_METHODS)[number]
// A refund's request key: Hub's money request id, so every attempt to carry
// out the same approved refund is the same refund in Booking (its
// payment_refunds.request_key is unique). Never reused for another refund.
export const REFUND_REQUEST_KEY = /^hub:[A-Za-z0-9_-]{8,100}$/
export const InvoiceLineInput = z.object({ description: Text(1, 500), quantity: Quantity, unitMinor: Minor, category: OptText(100) })

// The short reference Booking keeps for a deliverable's evidence (ADR 0046):
// Hub keeps the evidence itself (the document, the photo, the BTownTix
// order it checked); Booking keeps a label, an https link the sponsor may
// open, and the reference, so myBRE can show "fulfilled, with proof".
export const DeliverableEvidenceRef = z
  .object({
    kind: z.enum(['document', 'photo', 'link', 'btowntix_order']),
    label: Text(1, 200),
    url: z.string().trim().max(2000).url().refine((u) => u.startsWith('https://'), 'An https link').nullable().optional(),
    ref: z.string().trim().max(200).nullable().optional(),
  })
  .refine((e) => e.kind !== 'link' || !!e.url, 'A link needs its url')
  .refine((e) => e.kind !== 'btowntix_order' || !!e.ref, 'A BTownTix order needs its order id')
export type DeliverableEvidenceRef = z.infer<typeof DeliverableEvidenceRef>

export const BookingCommandPayloads = {
  'booking.rental.create': z.object({
    clientName: Text(1, 200),
    clientEmail: Email,
    clientPhone: OptText(40),
    clientOrg: OptText(200),
    companyId: Id.nullable().optional(),
    eventName: Text(1, 200),
    eventType: OptText(100),
    expectedAttendance: z.number().int().min(0).max(1_000_000).nullable().optional(),
    setupNotes: OptText(4000),
    internalNotes: OptText(4000),
    ownerSub: Sub.nullable().optional(),
  }),
  'booking.rental.owner.set': z.object({ ownerSub: Sub.nullable() }),
  'booking.rental.status.set': z.object({ status: z.enum(['inquiry', 'estimate', 'cancelled']) }),
  'booking.rental.stage_move': z.object({ toStage: z.literal('inquiry') }),
  'booking.sponsorship.stage_move': z.object({ toStage: z.enum(['prospect', 'proposal']) }),
  'booking.rental.renter.set': z.object({
    clientName: Text(1, 200),
    clientEmail: Email,
    clientPhone: OptText(40),
    clientOrg: OptText(200),
    companyId: Id.nullable().optional(),
  }),
  'booking.rental.details.update': z
    .object({
      eventName: Text(1, 200).optional(),
      eventType: OptText(100),
      expectedAttendance: z.number().int().min(0).max(1_000_000).nullable().optional(),
      setupNotes: OptText(4000),
      internalNotes: OptText(4000),
    })
    .refine((p) => Object.keys(p).length > 0, 'Change at least one field'),
  'booking.rental.message.send': z.object({ body: Text(1, 5000) }),
  'booking.rental.mybre.invite': z.object({}),
  'booking.hold.place': z.object({
    spaceId: Id,
    date: Day,
    holdLevel: z.number().int().min(1).max(3),
    expiresOn: Day.nullable().optional(),
    notes: OptText(1000),
  }),
  'booking.hold.extend': z.object({ holdId: Id, expiresOn: Day }),
  'booking.hold.release': z.object({ holdId: Id }),
  'booking.sponsorship.create': z.object({
    sponsorName: Text(1, 200),
    sponsorContact: OptText(200),
    sponsorEmail: Email,
    sponsorPhone: OptText(40),
    packageName: OptText(200),
    eventName: OptText(200),
    description: OptText(4000),
    compensationType: z.enum(['cash', 'trade', 'cash_and_trade']),
    amountMinor: z.number().int().min(0).nullable().optional(),
    tradeValueMinor: z.number().int().min(0).nullable().optional(),
    tradeDetails: OptText(2000),
    startDate: Day.nullable().optional(),
    endDate: Day.nullable().optional(),
    internalNotes: OptText(4000),
    ownerSub: Sub.nullable().optional(),
  }),
  'booking.sponsorship.owner.set': z.object({ ownerSub: Sub.nullable() }),
  'booking.sponsorship.message.send': z.object({ body: Text(1, 5000) }),
  'booking.sponsorship.mybre.invite': z.object({}),
  'booking.company.ensure': z.object({ name: Text(1, 200) }),

  // ------------------------------------------------------------ Phase 4
  'booking.rental.space.add': z.object({ spaceId: Id, ...SpaceTimes }).superRefine(endsAfterStart),
  'booking.rental.space.update': z.object({ rentalSpaceId: Id, ...SpaceTimes }).superRefine(endsAfterStart),
  'booking.rental.space.remove': z.object({ rentalSpaceId: Id }),
  // The whole list, in order: a line with a sourceId is Booking's line
  // (changed if different), one without is new, and a Booking line not
  // listed is removed. One batch, so the quote never shows half a save.
  'booking.rental.lines.set': z.object({
    lines: z.array(InvoiceLineInput.extend({ sourceId: Id.optional() })).max(100),
  }),
  // Tax stays as Booking has it (BRE charges none, ADR 0039). A discount over
  // the threshold (discountNeedsApproval) must carry its approval.
  'booking.rental.pricing.set': z.object({
    discountMinor: Minor,
    discountNote: OptText(500),
    depositMinor: Minor.nullable(),
    depositDueDate: Day.nullable(),
    approval: BookingMoneyApproval.optional(),
    // With an approval: the subtotal the approver saw. Booking refuses
    // (STALE) when the quote's subtotal has changed since.
    approvedSubtotalMinor: Minor.optional(),
  }),
  'booking.rental.estimate.send': z.object({}),
  'booking.rental.contract.send': z.object({}),
  'booking.rental.contract.countersign': z.object({}),
  'booking.rental.coi.verify': z.object({
    insurer: OptText(200),
    coverageMinor: Minor.nullable().optional(),
    effectiveDate: Day.nullable().optional(),
    expirationDate: Day.nullable().optional(),
    note: OptText(1000),
  }),
  'booking.rental.coi.reject': z.object({ reason: Text(1, 1000) }),
  'booking.rental.coi.request': z.object({}),
  'booking.rental.insurance.offers.set': z.object({ liabilityOffered: z.boolean(), cancellationOffered: z.boolean().optional() }),
  'booking.rental.document.request': z.object({ title: Text(1, 200), description: OptText(2000), dueDate: Day.nullable().optional() }),
  'booking.rental.document.review': z.object({ requestId: Id, decision: z.enum(['approved', 'waived']) }),
  'booking.sponsorship.agreement.terms.set': z.object({ termIds: z.array(Id).max(200) }),
  'booking.sponsorship.agreement.send': z.object({}),

  // ------------------------------------------------------------ Phase 5
  // The bill-to name, email and address come from the record, as Booking's
  // new-invoice screen fills them. No discount or tax on an invoice from Hub.
  'booking.invoice.create': z.object({ lines: z.array(InvoiceLineInput).min(1).max(50), dueDate: Day, notes: OptText(2000) }),
  'booking.invoice.approve': z.object({ invoiceId: Id, note: OptText(1000) }),
  'booking.invoice.send': z.object({ invoiceId: Id }),
  'booking.invoice.remind': z.object({ invoiceId: Id }),
  // Refused while the invoice has money on it: refund first.
  'booking.invoice.void': z.object({ invoiceId: Id, reason: Text(1, 1000) }),
  'booking.invoice.collection.update': z.object({ invoiceId: Id, collectionStatus: z.enum(BOOKING_COLLECTION_STATUSES), nextFollowUpOn: Day.nullable(), note: OptText(1000) }),
  // Who recorded it is the actor (Booking's received_by). A check number or
  // wire reference already recorded on this invoice is refused (CONFLICT)
  // unless the person says it really is a second payment.
  'booking.payment.record_offline': z.object({
    invoiceId: Id,
    amountMinor: PositiveMinor,
    method: z.enum(OFFLINE_PAYMENT_METHODS),
    reference: OptText(100),
    receivedOn: Day,
    note: OptText(1000),
    allowDuplicateReference: z.boolean().optional(),
  }),
  // Carried out only after a Hub approval by someone other than the
  // requester (decision 6). Not version-checked against the record: Booking
  // checks expectedRefundedMinor against what is already refunded or in
  // flight on that payment (STALE otherwise), never refunds more than was
  // paid (LIMIT), and treats a second attempt with the same requestKey as
  // the same refund: it resumes or reports it, never refunds twice.
  'booking.refund.execute': z.object({
    invoiceId: Id,
    paymentId: Id,
    amountMinor: PositiveMinor,
    method: z.enum(REFUND_METHODS),
    reason: Text(1, 1000),
    requestKey: z.string().regex(REFUND_REQUEST_KEY),
    approval: BookingMoneyApproval,
    expectedRefundedMinor: Minor,
  }),
  'booking.sponsorship.invoices.generate': z.object({}),

  // ---------------------------------------------------------------- Phase 6
  'booking.rental.closeout.save': z.object({
    status: z.enum(['pending', 'in_review']),
    inspectionNotes: OptText(4000),
    damageNotes: OptText(4000),
    followUpNotes: OptText(4000),
    // Absent keeps Booking's figure (it is hidden from people without money rights).
    recommendedChargeMinor: Minor.nullable().optional(),
  }),
  'booking.rental.closeout.complete': z.object({
    depositDisposition: z.enum(['applied', 'returned', 'none']),
    waivers: z.array(z.object({ key: z.enum(['insurance', 'documents', 'inspection']), reason: Text(1, 500) })).max(3),
    note: OptText(1000),
  }),

  // ---------------------------------------------------------------- Phase 7
  'booking.deliverable.add': z.object({
    itemId: Id.nullable().optional(),
    description: Text(1, 500),
    dueDate: Day.nullable().optional(),
    kind: z.enum(['standard', 'ticket_allotment']).optional(),
    quantity: z.number().int().min(1).max(100_000).nullable().optional(),
    ownerSub: Sub.nullable().optional(),
  }),
  'booking.deliverable.update': z
    .object({
      deliverableId: Id,
      status: z.enum(['open', 'in_progress', 'fulfilled', 'waived']).optional(),
      ownerSub: Sub.nullable().optional(),
      dueDate: Day.nullable().optional(),
      quantity: z.number().int().min(1).max(100_000).nullable().optional(),
      description: Text(1, 500).optional(),
      evidence: DeliverableEvidenceRef.nullable().optional(),
    })
    .refine((p) => Object.keys(p).length > 1, 'Change at least one field.'),
  'booking.deliverable.review': z.object({ deliverableId: Id, decision: z.enum(['approve', 'revise']), notes: OptText(2000) }),
  'booking.deliverable.remove': z.object({ deliverableId: Id }),
  'booking.sponsorship.renew': z.object({}),
  'booking.sponsorship.renewal.set': z
    .object({
      renewalStatus: z.enum(['upcoming', 'active', 'waived', 'exercised', 'expired']).nullable().optional(),
      renewalType: z.enum(['none', 'standard', 'fror', 'exclusive_window']).optional(),
      renewalDate: Day.nullable().optional(),
      windowStart: Day.nullable().optional(),
      windowEnd: Day.nullable().optional(),
      notes: OptText(2000),
    })
    .refine((p) => Object.values(p).some((v) => v !== undefined), 'Change at least one field.')
    .refine((p) => !(p.windowStart && p.windowEnd && p.windowEnd < p.windowStart), 'The renewal window must end after it starts.'),
  'booking.mybre.invite_batch': z.object({
    previewHash: z.string().min(1).max(128).regex(/^[A-Za-z0-9_:.-]+$/),
    max: z.number().int().min(1).max(INVITE_BATCH_CAP).optional(),
  }),
  'booking.interest.convert': z.object({
    submissionId: Id,
    ownerSub: Sub.nullable().optional(),
    compensationType: z.enum(['cash', 'trade', 'cash_and_trade']).optional(),
    amountMinor: Minor.nullable().optional(),
    tradeValueMinor: Minor.nullable().optional(),
    startDate: Day.nullable().optional(),
    endDate: Day.nullable().optional(),
  }),
} as const satisfies Record<BookingCommandType, z.ZodType>

export type BookingCommandPayload<T extends BookingCommandType> = z.infer<(typeof BookingCommandPayloads)[T]>

export type BookingCommandActor = { personId: string; displayName: string; sub: string }

export type BookingCommand<T extends BookingCommandType = BookingCommandType> = {
  version: 1
  commandId: string
  type: T
  actor: BookingCommandActor
  target: { kind: BookingCommandTargetKind; id: string } | null
  // Required by every command on an existing record: the version the
  // person saw (Hub's read of the record).
  expected?: { version: string }
  payload: BookingCommandPayload<T>
}

// ------------------------------------------------------------- the answers

// Booking's answer about one space on one day, from its own conflict check
// (space-availability.ts). `asOf` is when Booking looked. Hub shows it as
// "Booking says … at <time>", never as its own claim, and an unavailable
// Booking is "unknown", never free.
export const AVAILABILITY_STATES = ['free', 'held', 'booked', 'blocked'] as const
export const BookingAvailabilityItem = z.object({
  type: z.enum(['rental', 'show', 'hold', 'blackout']),
  name: z.string().max(500),
  firm: z.boolean(),
})
export const BookingAvailabilityDay = z.object({
  date: Day,
  state: z.enum(AVAILABILITY_STATES),
  items: z.array(BookingAvailabilityItem).max(50),
})
export type BookingAvailabilityDay = z.infer<typeof BookingAvailabilityDay>
export const BookingAvailabilityAnswer = z.object({
  asOf: z.string().max(40),
  spaceId: Id,
  date: Day,
  state: z.enum(AVAILABILITY_STATES),
  items: z.array(BookingAvailabilityItem).max(50),
})
export type BookingAvailabilityAnswer = z.infer<typeof BookingAvailabilityAnswer>

//   GET /api/internal/hub/v1/availability?space_ids=<id>,<id>&from=YYYY-MM-DD&to=YYYY-MM-DD   (read key)
export const AVAILABILITY_MAX_DAYS = 31
export const AVAILABILITY_MAX_SPACES = 20
export const BookingAvailabilityList = z.object({
  version: z.literal(1),
  asOf: z.string().max(40),
  from: Day,
  to: Day,
  spaces: z
    .array(z.object({ spaceId: Id, venueName: z.string().max(500).nullable(), spaceName: z.string().max(500).nullable(), days: z.array(BookingAvailabilityDay).max(AVAILABILITY_MAX_DAYS) }))
    .max(AVAILABILITY_MAX_SPACES),
})
export type BookingAvailabilityList = z.infer<typeof BookingAvailabilityList>

//   GET /api/internal/hub/v1/staff   (read key): Booking's staff users who
// have a BRE account, with their Booking role, for Hub's owner picker and
// for the refusal that names the role someone needs. No emails.
export const BookingStaffList = z.object({
  version: z.literal(1),
  asOf: z.string().max(40),
  staff: z.array(z.object({ sub: Sub, name: z.string().max(200).nullable(), role: z.enum(BOOKING_ROLES) })).max(500),
})
export type BookingStaffList = z.infer<typeof BookingStaffList>

//   GET /api/internal/hub/v1/commands?kind=rental&id=<id>   (read key):
// Booking's command log for one record, newest first (both channels).
export const BookingCommandLogEntry = z.object({
  commandId: z.string().max(64),
  channel: z.enum(['hub', 'mybre', 'booking']),
  type: z.string().max(80),
  status: z.enum(['applied', 'refused']),
  code: z.string().max(40).nullable(),
  actorKind: z.enum(['staff', 'customer']),
  actorSub: z.string().max(200).nullable(),
  actorName: z.string().max(200).nullable(),
  hubRequestId: z.string().max(80).nullable(),
  versionBefore: z.string().max(20).nullable(),
  versionAfter: z.string().max(20).nullable(),
  receivedAt: z.string().max(40),
})
export type BookingCommandLogEntry = z.infer<typeof BookingCommandLogEntry>
export const BookingCommandLog = z.object({
  version: z.literal(1),
  asOf: z.string().max(40),
  entries: z.array(BookingCommandLogEntry).max(200),
})
export type BookingCommandLog = z.infer<typeof BookingCommandLog>

// What `result` holds after 'applied' or 'duplicate'. Every command on a
// record answers the record's new version; a create answers the new id.
export const BookingCommandResultBody = z.object({
  record: z.object({ kind: z.enum(['rental', 'sponsorship', 'company']), id: Id, version: z.string().max(20).nullable() }).nullable(),
  holdId: Id.nullable().optional(),
  availability: BookingAvailabilityAnswer.nullable().optional(),
  // Booking's provisional items on that space and day (a second hold, an
  // inquiry), for the person to see. Never blocking.
  warnings: z.array(z.string().max(500)).max(20).optional(),
  // The invite went to this address (the record's own email).
  sentTo: z.string().max(320).nullable().optional(),
  created: z.boolean().optional(),
  // Phase 4 and 5. The record's status when the command moved it (an
  // estimate sent from inquiry, a contract sent), the room or document
  // request it made, and the invoice, payment or refund it touched, as
  // Booking reports them. A refund's status is Booking's: 'pending' until
  // Stripe (or, for money returned by hand, Booking) settles it. Hub shows
  // nothing as refunded or paid until Booking says 'succeeded'.
  status: z.string().max(40).optional(),
  rentalSpaceId: Id.nullable().optional(),
  documentRequestId: Id.nullable().optional(),
  countersignedAt: z.string().max(40).nullable().optional(),
  invoice: z.object({ sourceId: Id, number: z.string().max(40).nullable(), status: z.string().max(40) }).nullable().optional(),
  invoicesCreated: z.number().int().min(0).max(500).optional(),
  payment: z.object({ sourceId: Id, status: z.string().max(40), amountMinor: z.number().int() }).nullable().optional(),
  refund: z
    .object({
      sourceId: Id,
      status: z.enum(['pending', 'succeeded', 'failed']),
      amountMinor: z.number().int(),
      method: z.string().max(20),
      message: z.string().max(1000).nullable().optional(),
      // True when this attempt found the refund already made (the same
      // requestKey): nothing new happened in Booking or Stripe.
      resumed: z.boolean().optional(),
    })
    .nullable()
    .optional(),
  // Phase 6 and 7: the checklist Booking worked out when it closed the
  // record, the deliverable a command made or changed, the renewal offer it
  // made, and the sponsorship an interest submission became.
  checklist: z.array(z.object({ key: z.string().max(40), label: z.string().max(200), state: z.string().max(20), detail: z.string().max(2000), waivable: z.boolean(), waiverReason: z.string().max(500).nullable().optional() })).max(20).nullable().optional(),
  deliverableId: Id.nullable().optional(),
  renewalId: Id.nullable().optional(),
  sponsorshipId: Id.nullable().optional(),
  // Phase 8: what booking.mybre.invite_batch did, item by item.
  batch: BookingInviteBatchResult.optional(),
})
export type BookingCommandResultBody = z.infer<typeof BookingCommandResultBody>

export const BOOKING_REFUSAL_CODES = [
  'STALE',
  'COMMAND_REUSED',
  'FORBIDDEN',
  'CONFLICT',
  'INVALID',
  'NOT_FOUND',
  'UNKNOWN_COMMAND',
  'UNAUTHORIZED',
  'WRONG_ENVIRONMENT',
  'DISABLED',
  // Phase 5. A discount over the threshold (or a refund) without its Hub
  // approval; a refund over what is left on the payment; and a provider
  // outcome Booking doesn't know yet (rule 12: never retried blindly; ask
  // Booking again with the same requestKey).
  'APPROVAL_REQUIRED',
  'LIMIT',
  'UNKNOWN_OUTCOME',
] as const
export type BookingRefusalCode = (typeof BOOKING_REFUSAL_CODES)[number]

export type BookingCommandRefusal = {
  error: {
    code: BookingRefusalCode
    message: string
    // STALE: the record's current version. CONFLICT: what is in the way.
    current?: { version: string | null; refundedMinor?: number; refundableMinor?: number; subtotalMinor?: number }
    conflicts?: { type: string; name: string; firm: boolean }[]
    availability?: BookingAvailabilityAnswer
    // FORBIDDEN: the Booking capability and the roles that have it.
    needed?: { capability: BookingCapability; roles: BookingRole[] }
    // INVALID from booking.rental.closeout.complete: the checklist as Booking worked it out.
    checklist?: { key: string; label: string; state: string; detail: string; waivable: boolean; waiverReason?: string | null }[]
  }
}

// ------------------------------------------------------------ Hub's side

// What Booking's answer becomes in Hub (the adapter's sendBookingCommand,
// the web Worker's route and the browser). `unknown` means the request may
// or may not have reached Booking: retrying with the SAME commandId is safe,
// because Booking replays the first result instead of applying again.
export type BookingCommandRefusalDetail = BookingCommandRefusal['error']
export type BookingCommandOutcome =
  | { outcome: 'applied' | 'duplicate'; result: BookingCommandResultBody }
  | ({ outcome: 'refused'; status: number } & Omit<BookingCommandRefusalDetail, 'code'> & { code: BookingRefusalCode | 'REFUSED' })
  | { outcome: 'unknown'; message: string }
  | { outcome: 'not_configured'; message: string }
export type BookingCommandOutcomeKind = BookingCommandOutcome['outcome']
export const BOOKING_COMMAND_OUTCOMES = ['applied', 'duplicate', 'refused', 'unknown', 'not_configured'] as const

export const BookingCommandRefusalBody = z.object({
  error: z.object({
    code: z.string().max(40),
    message: z.string().max(2000),
    current: z
      .object({ version: z.string().max(20).nullable(), refundedMinor: z.number().int().optional(), refundableMinor: z.number().int().optional(), subtotalMinor: z.number().int().optional() })
      .optional(),
    conflicts: z.array(z.object({ type: z.string().max(40), name: z.string().max(500), firm: z.boolean() })).max(50).optional(),
    availability: BookingAvailabilityAnswer.optional(),
    needed: z.object({ capability: z.enum(BOOKING_CAPABILITIES), roles: z.array(z.enum(BOOKING_ROLES)).max(10) }).optional(),
    // Phase 6: the closeout checklist that stopped the record closing.
    checklist: z.array(z.object({ key: z.string().max(40), label: z.string().max(200), state: z.string().max(20), detail: z.string().max(2000), waivable: z.boolean(), waiverReason: z.string().max(500).nullable().optional() })).max(20).optional(),
  }),
})

export const BookingCommandSuccessBody = z.object({
  version: z.literal(1),
  commandId: z.string().max(64),
  status: z.enum(['applied', 'duplicate']),
  result: BookingCommandResultBody,
})

// What the browser sends the web Worker. commandId is made in the browser
// once per attempt and reused on a retry, so a retry after "unknown" can
// never apply twice.
export const BookingCommandRequest = z.object({
  commandId: z.string().uuid(),
  type: z.string().refine(isBookingCommandType, 'Unknown command'),
  target: z.object({ kind: z.enum(['rental', 'sponsorship', 'company']), id: Id }).nullable(),
  expected: z.object({ version: z.string().regex(/^\d{1,15}$/) }).optional(),
  payload: z.unknown(),
})
export type BookingCommandRequest = { commandId: string; type: BookingCommandType; target: { kind: BookingCommandTargetKind; id: string } | null; expected?: { version: string }; payload: unknown }

// The web Worker's answer: Booking's outcome, the command id it used, and
// the record to open after a create.
export type BookingCommandResponse = BookingCommandOutcome & { commandId: string; type: BookingCommandType; hubLink?: HubRecordLinkOutcome }

// The Hub person and organization staff picked on a form (ADR 0078). Sent
// beside a command, never to Booking: Hub ties them after it applies.
// Undefined leaves a side as it is; null clears it.
export const HubRecordLink = z.object({ personId: z.string().min(1).max(64).nullable().optional(), organizationId: z.string().min(1).max(64).nullable().optional() })
export type HubRecordLink = z.infer<typeof HubRecordLink>
export const HUB_LINK_COMMANDS: readonly string[] = ['booking.rental.create', 'booking.rental.renter.set', 'booking.sponsorship.create', 'booking.interest.convert']
export type HubRecordLinkOutcome = { status: 'linked'; contactElsewhere: { personId: string; displayName: string } | null } | { status: 'failed'; message: string }

// Whether this Hub sends Booking commands (HUB_BOOKING_COMMANDS and both
// keys), and if not, why.
export type BookingCommandsStatus = {
  enabled: boolean
  reason: string | null
  // Money commands and sending approved refunds and discounts (Phase 5,
  // HUB_BOOKING_MONEY): off while commands are on means only the money is off.
  money?: { enabled: boolean; reason: string | null }
}

// One row of Hub's own record of a command a person sent (core's
// booking_command), whatever came of it.
export type HubBookingCommandEntry = {
  id: string
  commandId: string
  type: string
  label: string
  record: { kind: BookingCommandTargetKind; id: string } | null
  actor: { personId: string | null; displayName: string | null }
  outcome: BookingCommandOutcomeKind
  code: string | null
  message: string | null
  expectedVersion: string | null
  resultVersion: string | null
  requestId: string | null
  createdAt: string
}

// The Commands tab: Hub's log beside Booking's.
export type BookingCommandLogView = {
  hub: HubBookingCommandEntry[]
  booking: { status: 'ok'; asOf: string; entries: BookingCommandLogEntry[] } | { status: 'unavailable' | 'not_configured' | 'denied'; asOf: string; reason: string }
}

export const bookingCommandLabel = (type: string): string => (isBookingCommandType(type) ? BOOKING_COMMANDS[type].label : type)

// The record a command is about: its target, or what a create made.
export function bookingCommandRecord(target: BookingCommand['target'], result: BookingCommandResultBody | null): { kind: BookingCommandTargetKind; id: string } | null {
  if (target) return target
  return result?.record ? { kind: result.record.kind, id: result.record.id } : null
}

// ------------------------------------------------------- the customer channel
//
// Designed now so Phase 3's myBRE portal can call the same domain functions;
// Booking answers 503 DISABLED to every request until its MYBRE_CHANNEL
// switch is "on" (Phase 3). The secret is BOOKING_CUSTOMER_SECRET (myBRE's
// Next server and Booking only; never Hub's, never the read key).
//
//   canonical = ['bre-booking-customer-v1', METHOD, pathWithQuery, sub, timestamp, nonce, sha256Hex(body)].join('\n')
//   signature = base64url(HMAC-SHA256(BOOKING_CUSTOMER_SECRET, canonical))
//   headers   x-mybre-sub, x-mybre-sid, x-mybre-timestamp, x-mybre-nonce,
//             x-mybre-signature, x-mybre-client-ip, x-mybre-client-ua
//
// Five-minute window, nonce kept once, constant-time compare. Booking checks
// the proven link between the asserted subject and the record's contact on
// every request (plan §6.3) and logs each command in the same
// source_commands table with channel 'mybre' and actor kind 'customer'.
export const BOOKING_CUSTOMER_PREFIX = '/api/internal/mybre/v1'
export const BOOKING_CUSTOMER_SIGNATURE_VERSION = 'bre-booking-customer-v1'

// The customer commands whose Booking domain functions exist after Phase 2
// (the portal's own form actions call them today), and the ones Phase 3
// still has to extract. Every one is refused while the channel is off.
export const CUSTOMER_COMMANDS_READY = [
  'customer.message.send',
  'customer.contract.sign',
  'customer.questionnaire.submit',
  'customer.details.update',
] as const
export const CUSTOMER_COMMANDS_PHASE_3 = [
  'customer.records',
  'customer.rental.get',
  'customer.sponsorship.get',
  'customer.invite.accept',
  'customer.estimate.accept',
  'customer.contract.document',
  'customer.agreement.sign',
  'customer.payment.prepare',
  'customer.payment.status',
  'customer.payment_method.setup',
  'customer.payment_method.remove',
  'customer.receipt',
  'customer.coi.upload',
  'customer.insurance.quote',
  'customer.insurance.purchase',
  'customer.document.upload',
  'customer.document.get',
  'customer.message.list',
  'customer.deliverable.submit',
  'customer.sponsorship.renew.accept',
  'customer.inquiry.create',
] as const

// ------------------------------------------------------------ contract pin

// The command table both sides must agree on (plan §7.1.9): each command,
// the record kinds it takes, the Booking capability it needs and whether it
// carries expected.version. Booking's booking-commands.test.js builds the
// same lines from its own COMMAND_SPECS and checks the same SHA-256, so a
// command added or changed on one side only fails a test on the other.
export function bookingCommandTableCanonical(): string {
  return BOOKING_COMMAND_TYPES.map((type) => {
    const spec = BOOKING_COMMANDS[type]
    return [type, bookingCommandTargets(type).join('|') || '-', spec.bookingCapability, spec.needsExpected ? '1' : '0'].join(' ')
  })
    .sort()
    .join('\n')
}
export const BOOKING_COMMAND_TABLE_SHA256 = 'e3991a1aa5fc3be8725a23e2bc3d7bec163c0441d70b50b53e433d06b241ab8e'
